import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Play, Pause, RotateCcw, Volume2, VolumeX, Maximize2, Minimize2, Tv, RefreshCw, AlertCircle, MicOff } from "lucide-react";
import { TurfTalk, AuthExpiredError, mediaContentUrl } from "@/api/client";
import { CHAR_ORDER, CHAR_COLOR, CHAR_NAME, PUPPET } from "@/lib/tokens";
import { CharacterRig } from "@/components/CharacterRig";
import { audioPlayable, audioStateMessage } from "@/lib/characterAudio";
import { extractPredictions, resolveCharacterShow, episodeRenderGuard, predictionCardsFromCharacters } from "@/lib/sports";
import { trackShowStart, trackShowComplete } from "@/lib/telemetry";

// Independent per-character prediction cards. Renders each card EXACTLY from its
// own stored values (pick / confidence / reasoning) — no shared winner inferred.
function PredictionGrid({ cards, nameFor }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {cards.map((c) => (
        <div key={c.key || c.speaker} className="rounded-lg bg-black/30 border border-white/10 p-3" style={{ borderTopColor: CHAR_COLOR[c.speaker] || "#2A3544", borderTopWidth: 3 }} data-testid={`prediction-${c.speaker}`}>
          <div className="font-display text-sm text-white">{c.name || (nameFor ? nameFor(c.speaker) : c.speaker)}</div>
          <div className="mt-1.5 flex items-baseline gap-2">
            <span className="font-display text-lg" style={{ color: CHAR_COLOR[c.speaker] || "#fff" }} data-testid={`prediction-pick-${c.speaker}`}>{c.pick || "—"}</span>
            {c.confidence != null && <span className="text-[11px] text-white/50 tabnums" data-testid={`prediction-confidence-${c.speaker}`}>{Math.round(c.confidence * 100)}% conf</span>}
          </div>
          {(c.ats || c.ou) && <div className="text-[11px] text-white/45 tabnums mt-0.5">{[c.ats ? `ATS ${c.ats}` : null, c.ou ? `O/U ${c.ou}` : null].filter(Boolean).join(" · ")}</div>}
          {c.reasoning && <p className="text-xs text-white/60 mt-1.5 leading-snug" data-testid={`prediction-reasoning-${c.speaker}`}>{c.reasoning}</p>}
        </div>
      ))}
    </div>
  );
}

/**
 * Turf Talk Character Show — web.
 * Audio is the APPROVED branded pipeline ONLY: it plays the cached per-line
 * character audio (signed URL) from the canonical backend and drives the mouth
 * from that line's amplitude envelope. There is NO browser/Web-Speech voice and
 * NO placeholder/fallback voice. If branded audio is not provisioned, the show
 * renders an honest "voice audio unavailable" state and does not fake speech.
 */
export function CharacterShow({ gameId, game = null }) {
  const [ep, setEp] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [showStatus, setShowStatus] = useState(null);
  const [canonicalPicks, setCanonicalPicks] = useState([]);
  const [predChars, setPredChars] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [active, setActive] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [stageW, setStageW] = useState(0);
  const [progress, setProgress] = useState(0);
  const [level, setLevel] = useState(0);
  const [audioFailed, setAudioFailed] = useState(false);

  const stageRef = useRef(null);
  const audioRef = useRef(null);
  const nextRef = useRef(null); // { index, audio } — the ONE preloaded next clip
  const rafRef = useRef(null);
  const activeRef = useRef(-1);
  const showStartMsRef = useRef(0);
  const showStartedRef = useRef(false);
  const playGenRef = useRef(0); // bumps on teardown/stop/replay/switch/unmount — stales old play promises & callbacks
  const reqGenRef = useRef(0);  // bumps per load() — stales out-of-order characterShow/gamePredictions responses
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => { activeRef.current = active; }, [active]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const h = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", h);
    return () => mq.removeEventListener?.("change", h);
  }, []);

  const load = useCallback(() => {
    const reqGen = ++reqGenRef.current; // supersede any in-flight load
    setLoading(true); setLoadError(false);
    TurfTalk.characterShow(gameId)
      .then((r) => {
        if (reqGen !== reqGenRef.current || !mountedRef.current) return; // stale/unmounted — ignore
        const s = resolveCharacterShow(r);
        setEp(s.episode); setCoverage(s.coverage); setShowStatus(s.status); setCanonicalPicks(s.canonicalPicks);
      })
      .catch((e) => { if (reqGen !== reqGenRef.current || !mountedRef.current) return; if (!(e instanceof AuthExpiredError)) setLoadError(true); })
      .finally(() => { if (reqGen === reqGenRef.current && mountedRef.current) setLoading(false); });
    // Independent, always-current locked prediction cards (v5). Used as the
    // truthful pick source whenever no valid current show is renderable.
    TurfTalk.gamePredictions(gameId)
      .then((r) => { if (reqGen !== reqGenRef.current || !mountedRef.current) return; setPredChars(r.characters || []); })
      .catch(() => { if (reqGen === reqGenRef.current && mountedRef.current) setPredChars([]); });
  }, [gameId]);
  useEffect(() => { load(); }, [load]);

  const predCards = useMemo(() => predictionCardsFromCharacters(predChars, game), [predChars, game]);

  useEffect(() => {
    if (!stageRef.current) return;
    const ro = new ResizeObserver((entries) => setStageW(entries[0].contentRect.width));
    ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, [ep, fullscreen]);

  const lines = useMemo(() => ep?.lines ?? [], [ep]);
  const audio = ep?.audio || null;
  const voiceReady = audioPlayable(audio);
  const totalMs = useMemo(() => lines.reduce((a, l) => a + (l.duration_ms || 0), 0), [lines]);
  const cum = useMemo(() => {
    const out = []; let s = 0;
    for (const l of lines) { out.push(s); s += l.duration_ms || 0; }
    out.push(s);
    return out;
  }, [lines]);

  const stopRaf = () => { if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; } };

  const clearNext = () => {
    const n = nextRef.current;
    if (n && n.audio) { n.audio.onerror = null; n.audio.oncanplaythrough = null; try { n.audio.pause(); n.audio.removeAttribute("src"); n.audio.load(); } catch { /* noop */ } }
    nextRef.current = null;
  };

  const teardown = useCallback(() => {
    playGenRef.current += 1; // invalidate any pending play promise / queued callback
    stopRaf();
    const a = audioRef.current;
    if (a) { a.onended = null; a.onerror = null; try { a.pause(); a.removeAttribute("src"); a.load(); } catch { /* noop */ } }
    clearNext();
    setLevel(0);
  }, []);

  // A gameId switch tears down the OLD audio IMMEDIATELY (synchronously on the
  // change), not after the new episode resolves — so no old clip keeps playing
  // while the next show loads. Never autoplays; the user must press play.
  useEffect(() => {
    teardown();
    setPlaying(false); setActive(-1); setEnded(false); setProgress(0); setAudioFailed(false);
    showStartedRef.current = false;
  }, [gameId, teardown]);

  // An in-place episode replacement (manual refresh/retry, same game) also
  // resets the cursor and drops any buffered clip.
  useEffect(() => {
    teardown();
    setPlaying(false); setActive(-1); setEnded(false); setProgress(0); setAudioFailed(false);
    showStartedRef.current = false;
  }, [ep, teardown]);

  useEffect(() => () => teardown(), [teardown]);

  const finish = useCallback(() => {
    teardown(); setPlaying(false); setActive(-1); setEnded(true); setProgress(100);
    if (showStartedRef.current) { trackShowComplete(gameId, Date.now() - showStartMsRef.current); showStartedRef.current = false; }
  }, [teardown, gameId]);

  // Fire character_show_start once per playthrough when playback actually begins.
  const markShowStart = useCallback(() => {
    if (showStartedRef.current) return;
    showStartedRef.current = true; showStartMsRef.current = Date.now();
    trackShowStart(gameId);
  }, [gameId]);

  const sample = useCallback(() => {
    const a = audioRef.current;
    const i = activeRef.current;
    if (!a || i < 0) return;
    const lineAudio = audio.lines.find((l) => l.index === i);
    const env = lineAudio?.env || [];
    const durMs = (lineAudio?.duration_ms || lines[i]?.duration_ms || (a.duration * 1000) || 4000);
    const tMs = a.currentTime * 1000;
    if (env.length) {
      const idx = Math.min(env.length - 1, Math.max(0, Math.floor((tMs / durMs) * env.length)));
      setLevel(env[idx] ?? 0);
    }
    setProgress(totalMs ? Math.min(100, ((cum[i] + tMs) / totalMs) * 100) : 0);
    rafRef.current = requestAnimationFrame(sample);
  }, [audio, lines, cum, totalMs]);

  // Preload ONLY the next existing clip while the current one plays, so the
  // transition doesn't pay a serial network-start cost. Bounded to one clip; the
  // element is muted and never played here (no autoplay, no new provider). An
  // unavailable next clip or a preload error degrades to a fetch-at-play.
  const preloadNext = useCallback((nextIndex) => {
    const cur = nextRef.current;
    if (cur && cur.index === nextIndex) return; // already warming exactly this clip
    clearNext();
    if (!voiceReady || !lines[nextIndex]) return;
    const lineAudio = audio.lines.find((l) => l.index === nextIndex);
    if (!lineAudio || !lineAudio.url) return; // next clip unavailable → graceful no-op
    const el = new Audio();
    el.preload = "auto";
    el.muted = true; // preload only: never audible, never autoplayed
    el.onerror = () => { if (nextRef.current && nextRef.current.audio === el) nextRef.current = null; };
    el.src = mediaContentUrl(lineAudio.url);
    try { el.load(); } catch { /* noop */ }
    nextRef.current = { index: nextIndex, audio: el };
  }, [voiceReady, lines, audio]);

  const playLine = useCallback((i) => {
    if (!voiceReady || !lines[i]) { if (!lines[i]) finish(); return; }
    setActive(i); setEnded(false);
    const lineAudio = audio.lines.find((l) => l.index === i);
    if (!lineAudio || !lineAudio.url) { setAudioFailed(true); setPlaying(false); teardown(); return; }

    const preloaded = nextRef.current;
    let a;
    if (preloaded && preloaded.index === i && preloaded.audio) {
      // Promote the clip we buffered during the previous line — no serial fetch.
      nextRef.current = null;
      const old = audioRef.current;
      if (old && old !== preloaded.audio) { old.onended = null; old.onerror = null; try { old.pause(); old.removeAttribute("src"); old.load(); } catch { /* noop */ } }
      a = preloaded.audio;
      a.onerror = null; // clear the preload-only handler; real handlers set below
    } else {
      a = audioRef.current;
      if (!a) { a = new Audio(); }
      a.src = mediaContentUrl(lineAudio.url);
    }
    audioRef.current = a;
    const gen = playGenRef.current; // this playback's generation
    a.onended = () => { if (playGenRef.current === gen && audioRef.current === a) playLine(i + 1); };
    a.onerror = () => { if (playGenRef.current === gen && audioRef.current === a && mountedRef.current) { setAudioFailed(true); setPlaying(false); teardown(); } };
    a.muted = muted;
    try { a.currentTime = 0; } catch { /* noop */ }
    const p = a.play();
    if (p?.catch) p.catch(() => { if (playGenRef.current === gen && audioRef.current === a && mountedRef.current) { setAudioFailed(true); setPlaying(false); } });
    stopRaf();
    rafRef.current = requestAnimationFrame(sample);
    preloadNext(i + 1); // warm ONLY the next clip while this one plays
  }, [voiceReady, lines, audio, muted, sample, finish, teardown, preloadNext]);

  const onPlayPause = useCallback(() => {
    if (!voiceReady) return;
    const a = audioRef.current;
    if (playing) {
      if (a) a.pause();
      stopRaf();
      clearNext(); // clean up the buffered next clip on pause (re-warmed on resume)
      setPlaying(false);
      return;
    }
    setAudioFailed(false);
    setPlaying(true);
    if (a && a.src && !ended && active >= 0) { // resume current line
      const gen = playGenRef.current;
      const p = a.play();
      if (p?.catch) p.catch(() => { if (playGenRef.current === gen && audioRef.current === a && mountedRef.current) setAudioFailed(true); });
      rafRef.current = requestAnimationFrame(sample);
      preloadNext(active + 1); // re-warm the next clip we dropped on pause
    } else {
      setEnded(false); setProgress(0); markShowStart(); playLine(0);
    }
  }, [voiceReady, playing, ended, active, playLine, sample, markShowStart, preloadNext]);

  const onReplay = useCallback(() => { teardown(); setEnded(false); setProgress(0); setPlaying(true); markShowStart(); playLine(0); }, [teardown, playLine, markShowStart]);
  const onMute = useCallback(() => { setMuted((m) => { const nm = !m; if (audioRef.current) audioRef.current.muted = nm; return nm; }); }, []);

  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [fullscreen]);

  // ---------- states ----------
  if (loading) return <div className="cs-stage grid place-items-center" style={{ aspectRatio: "16/10" }} data-testid="character-show-loading"><span className="text-white/50 text-sm">Loading Character Show…</span></div>;

  if (loadError) return (
    <div className="cs-stage grid place-items-center p-6" style={{ minHeight: 220 }} data-testid="character-show-error">
      <div className="text-center"><AlertCircle className="mx-auto mb-2 text-red-400" size={22} /><p className="text-white/80 text-sm mb-3">Couldn't load the Character Show.</p>
        <button onClick={load} className="px-4 py-2 rounded-lg bg-[#2F7D32] text-white text-sm inline-flex items-center gap-2" data-testid="show-error-refresh"><RefreshCw size={14} /> Retry</button></div>
    </div>
  );

  const guard = episodeRenderGuard(ep, gameId, game);
  const renderable = guard.renderable;
  const fallbackPicks = canonicalPicks.length ? canonicalPicks : predCards;
  const nameForFallback = (sp) => CHAR_NAME[sp] || sp;

  // No valid CURRENT show → truthful panel + the always-current locked picks.
  // Covers: no episode, snapshot mismatch, failed validation, pre-v4 (legacy),
  // previous-season, and wrong-matchup episodes. None of these ever play.
  if (!renderable) {
    let cov;
    if (showStatus === "prediction_snapshot_mismatch" || guard.reason === "snapshot_mismatch") {
      cov = { state: "prediction_snapshot_mismatch", title: "Archived show unavailable", detail: "This show is hidden because its picks don't match the verified prediction records. The locked character picks are shown below." };
    } else if (guard.reason === "validation_failed") {
      cov = { state: "validation_failed", title: "Current pregame show not ready", detail: "This show didn't pass fact validation and isn't shown. The locked character picks are shown below." };
    } else if (guard.reason === "legacy") {
      cov = { state: "legacy_hidden", title: "Current pregame show not ready", detail: "An earlier version of this show exists but isn't shown as the current pregame show. The locked character picks are shown below." };
    } else if (guard.reason === "season_stale") {
      cov = { state: "season_stale", title: "Current pregame show not ready", detail: "The available show is from a previous season and isn't shown here. The locked character picks are shown below." };
    } else if (guard.reason === "matchup_mismatch") {
      cov = { state: "matchup_mismatch", title: "Current pregame show not ready", detail: "The available show doesn't match this game and isn't shown. The locked character picks are shown below." };
    } else {
      cov = coverage ?? { state: "not_generated", title: "Pregame show not generated", detail: "A Character Show isn't available for this game yet." };
    }
    return (
      <div data-testid="character-show">
        <div className="cs-stage p-6 flex flex-col items-center justify-center text-center" style={{ minHeight: 200 }} data-testid={`show-coverage-${cov.state}`}>
          <Tv className="text-white/40 mb-2" size={26} />
          <p className="font-display text-xl text-white mb-1">{cov.title}</p>
          <p className="text-white/60 text-sm max-w-sm mb-4">{cov.detail}</p>
          <button onClick={load} className="px-4 py-2 rounded-lg tt-panel-3 text-white text-sm inline-flex items-center gap-2" data-testid="show-coverage-refresh"><RefreshCw size={14} /> Refresh</button>
        </div>
        <div className="mt-4 tt-panel rounded-xl p-4" data-testid="show-predictions">
          <div className="flex items-center justify-between mb-3">
            <span className="font-display tracking-widest text-xs text-white/50">CHARACTER PICKS</span>
            <span className="text-[10px] text-white/35">Locked · exhibition only</span>
          </div>
          {fallbackPicks.length
            ? <PredictionGrid cards={fallbackPicks} nameFor={nameForFallback} />
            : <p className="text-sm text-white/50" data-testid="show-predictions-pending">Verified character picks aren't available for this game yet.</p>}
        </div>
      </div>
    );
  }

  const activeLine = active >= 0 ? lines[active] : null;
  const Cw = Math.max(120, Math.round(stageW * 0.30));
  const slots = [0.05, 0.355, 0.66];

  const predictions = extractPredictions(ep);
  const nameFor = (speaker) => (lines.find((l) => l.speaker === speaker)?.speaker_name) || CHAR_NAME[speaker] || speaker;

  const cast = (
    <div className="absolute inset-0" data-testid="show-cast-layer" style={{ opacity: stageW ? 1 : 0, transition: "opacity 400ms ease" }}>
      {CHAR_ORDER.map((id, idx) => {
        const isActive = activeLine && id === activeLine.speaker;
        const rigW = Math.round(Cw * (PUPPET[id]?.aspect ?? 0.86));
        const homeX = slots[idx] * stageW;
        const centerNudge = isActive ? (stageW * 0.5 - (homeX + rigW / 2)) * 0.35 : 0;
        const scale = isActive ? 1.1 : (active >= 0 ? 0.9 : 1);
        const opacity = active < 0 ? 1 : (isActive ? 1 : 0.6);
        return (
          <div key={id} data-testid={`cast-${id}`} className="cs-cast-abs"
            style={{ left: homeX, width: Cw, transform: `translateX(${centerNudge}px) scale(${scale})`, opacity, zIndex: isActive ? 4 : 2, transformOrigin: "bottom center" }}>
            <div style={{ width: Cw, marginBottom: stageW * 0.11 }} className="mx-auto">
              <CharacterRig id={id} speaking={!!isActive && playing} level={isActive ? level : 0} reduced={reduced} playing={playing} width={rigW} />
            </div>
          </div>
        );
      })}
    </div>
  );

  const stage = (
    <div ref={stageRef} className="cs-stage" style={{ aspectRatio: fullscreen ? "auto" : "16/10", height: fullscreen ? "100%" : undefined }} data-testid="show-stage">
      <div className="absolute left-[8%] right-[8%] top-[10%] bottom-[34%] rounded-xl grid place-items-center" style={{ background: "linear-gradient(120deg, rgba(47,125,50,0.18), rgba(20,54,78,0.06))" }}>
        <div className="text-center leading-none"><div className="cs-wall-brand text-5xl md:text-7xl">TURF TALK</div><div className="cs-wall-brand text-base md:text-xl mt-1">PREGAME DEBATE</div></div>
      </div>
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-3 z-10">
        <span className="px-2.5 py-1 rounded-md bg-black/40 border border-white/10 text-[10px] tracking-widest text-white/90 font-display">CHARACTER SHOW</span>
        <span className="px-2.5 py-1 rounded-md bg-black/40 border border-white/10 text-white/90 font-display text-sm">{ep.matchup.away_abbr} <span className="text-white/40">@</span> {ep.matchup.home_abbr} · {String(ep.matchup.status).toUpperCase()}</span>
        <button onClick={() => setFullscreen((f) => !f)} data-testid="show-fullscreen" aria-label={fullscreen ? "Exit full screen" : "Full screen"} className="w-8 h-8 grid place-items-center rounded-md bg-black/40 border border-white/10 text-white hover:bg-black/60 transition-colors">{fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}</button>
      </div>
      {cast}
      {active < 0 && (
        <div className="absolute inset-x-0 top-[40%] -translate-y-1/2 text-center z-10 pointer-events-none" data-testid={ended ? "show-closing" : "show-establishing"}>
          <div className="text-[11px] tracking-[0.2em] text-[#7ec4e8] font-display">{ended ? "THAT'S OUR SHOW" : "PREGAME DEBATE"}</div>
          <div className="font-display text-3xl text-white mt-1">{ep.matchup.away_abbr} @ {ep.matchup.home_abbr}</div>
          <div className="text-white/55 text-sm mt-1">{ended ? "Replay the segment anytime" : voiceReady ? "Press play to start the segment" : "Voice audio not available for this show yet"}</div>
        </div>
      )}
      <div className="cs-desk"><div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#2F7D32]" /><span className="font-display tracking-[0.2em] text-white/85 text-sm">TURF TALK</span><span className="w-1.5 h-1.5 rounded-full bg-[#2F7D32]" /></div></div>
      <div className="absolute left-0 right-0 bottom-[4px] z-10 px-3 pb-2">
        <div className="flex items-center gap-2 mb-1.5">
          {activeLine ? (
            <>
              <div className="px-2.5 py-1 rounded-md font-display text-sm text-white" style={{ background: CHAR_COLOR[activeLine.speaker] }}>{activeLine.speaker_name} <span className="opacity-70 text-[10px]">· CHARACTER</span></div>
              <div className="px-2.5 py-1 rounded-md bg-black/50 border text-xs text-white tabnums" style={{ borderColor: CHAR_COLOR[activeLine.speaker] }} data-testid="show-pick-overlay">
                <span style={{ color: CHAR_COLOR[activeLine.speaker] }} className="font-display mr-1">LOCKED PICK</span>
                {activeLine.pick_overlay?.su_abbr}{activeLine.pick_overlay?.ats ? ` · ATS ${activeLine.pick_overlay.ats}` : ""}{activeLine.pick_overlay?.ou ? ` · ${activeLine.pick_overlay.ou}` : ""}
              </div>
            </>
          ) : <div className="px-2.5 py-1 rounded-md bg-black/50 border border-white/10 text-xs text-white/80" data-testid="show-pick-overlay">Pregame debate</div>}
        </div>
        <div className="rounded-lg bg-black/55 border border-white/10 px-3 py-2 min-h-[42px]" data-testid="show-caption"><p className="text-white text-sm leading-snug">{activeLine ? activeLine.caption : "Captions appear here as each character speaks."}</p></div>
      </div>
      <div className="cs-progress absolute left-0 right-0 bottom-0" data-testid="show-progress"><div style={{ width: `${progress}%`, transition: "width 120ms linear" }} /></div>
    </div>
  );

  const controls = voiceReady ? (
    <div className="flex items-center gap-2 mt-3">
      <button onClick={onPlayPause} data-testid="show-playpause" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white text-sm font-medium transition-colors">
        {playing ? <Pause size={16} /> : <Play size={16} />}{playing ? "Pause" : active >= 0 && !ended ? "Resume" : "Play show"}
      </button>
      <button onClick={onReplay} data-testid="show-replay" aria-label="Replay" className="w-9 h-9 grid place-items-center rounded-lg tt-panel-3 text-white hover:bg-[#26313f] transition-colors"><RotateCcw size={16} /></button>
      <button onClick={onMute} data-testid="show-mute" aria-label={muted ? "Unmute" : "Mute"} className="w-9 h-9 grid place-items-center rounded-lg tt-panel-3 hover:bg-[#26313f] transition-colors" style={{ color: muted ? "#F4B740" : "#fff" }}>{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
      <span className="ml-auto text-[11px] text-[#4ea152]">BRANDED VOICE · {audio.provider || "elevenlabs"}</span>
    </div>
  ) : (
    <div className="mt-3 tt-panel rounded-xl px-4 py-3 flex items-center gap-2.5" data-testid="show-voice-unavailable">
      <MicOff size={16} className="text-white/50 shrink-0" />
      <div className="text-white/70 text-sm">{audioStateMessage(audio?.state)}</div>
    </div>
  );

  const studio = (<div className="w-full">{stage}{controls}{audioFailed && voiceReady && <div className="mt-2 text-[11px] text-[#ff6b60]">Branded audio failed to play. Captions and transcript remain available below.</div>}</div>);

  return (
    <div data-testid="character-show">
      {!fullscreen ? studio : <div className="cs-stage grid place-items-center" style={{ minHeight: 160 }}><div className="text-center text-white/50"><Tv className="mx-auto mb-1" size={20} /><span className="text-sm">Playing in full screen</span></div></div>}
      {fullscreen && createPortal(
        <div className="fixed inset-0 z-[9999] bg-[#05080c] flex flex-col p-4" data-testid="show-fullscreen-overlay">
          <div className="flex items-center justify-between mb-3"><span className="font-display tracking-[0.2em] text-white">CHARACTER SHOW</span>
            <button onClick={() => setFullscreen(false)} data-testid="show-fullscreen-close" aria-label="Exit full screen" className="w-9 h-9 grid place-items-center rounded-lg tt-panel-3 text-white">✕</button></div>
          <div className="flex-1 min-h-0">{studio}</div>
        </div>, document.body)}
      <div className="mt-4 tt-panel rounded-xl p-4" data-testid="show-predictions">
        <div className="flex items-center justify-between mb-3">
          <span className="font-display tracking-widest text-xs text-white/50">CHARACTER PICKS</span>
        </div>
        {predictions.length ? (
          <PredictionGrid cards={predictions.map((p) => ({ ...p, key: p.speaker }))} nameFor={nameFor} />
        ) : (
          <p className="text-sm text-white/50" data-testid="show-predictions-pending">Character picks for this game haven't been generated yet.</p>
        )}
      </div>
      <div className="mt-4 tt-panel rounded-xl p-4" data-testid="show-transcript">
        <div className="font-display tracking-widest text-xs text-white/50 mb-2">TRANSCRIPT</div>
        <div className="space-y-1.5">{lines.map((l) => (<p key={l.index} className={`text-sm leading-snug ${active === l.index ? "text-white" : "text-white/60"}`}><span style={{ color: CHAR_COLOR[l.speaker] }} className="font-semibold">{l.speaker_name}: </span>{l.text}</p>))}</div>
      </div>
      <p className="text-[11px] text-white/40 mt-2">{ep.disclaimer}</p>
    </div>
  );
}
